import {
  ORGANIZATION_RULES,
  ORGANIZATION_UPDATED,
  plainRuleText,
  ruleCount,
  type RuleNode,
} from '@k-management/shared';
import { useMemo, useState } from 'react';
import { Icon } from '../../components/Icon.js';
import { usePageTitle } from '../../components/layout/page-chrome.js';
import { formatDate } from '../../lib/format.js';
import { RuleText } from './organization-ui.js';

/** Every topic with its path as key: «Offerta», «Offerta/Approvazione». */
function topicPaths(nodes: readonly RuleNode[], prefix = ''): string[] {
  return nodes.flatMap((node) =>
    node.children
      ? [`${prefix}${node.text}`, ...topicPaths(node.children, `${prefix}${node.text}/`)]
      : [],
  );
}

/**
 * Organizzazione › Regole (03/10/2026): the rules decided, as a tree of
 * topics. The search opens the branches holding the words searched.
 */
export function RulesPage() {
  usePageTitle('Regole');
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [search, setSearch] = useState('');
  const needle = search.trim().toLowerCase();
  const allTopics = useMemo(() => topicPaths(ORGANIZATION_RULES), []);

  const toggle = (path: string) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });

  const self = (node: RuleNode) => plainRuleText(node).toLowerCase().includes(needle);
  const matches = (node: RuleNode): boolean => self(node) || (node.children ?? []).some(matches);

  // A topic that matches shows all its rules; otherwise only the matching ones.
  const render = (
    nodes: readonly RuleNode[],
    level: number,
    prefix: string,
    inherited: boolean,
  ) => {
    const shown = nodes.filter((node) => !needle || inherited || matches(node));
    if (shown.length === 0) return null;
    return (
      <ul className={level === 0 ? 'org-rules' : undefined}>
        {shown.map((node) => {
          const path = `${prefix}${node.text}`;
          const isOpen = needle ? true : open.has(path);
          const tags = (
            <>
              {node.tag === 'block' && <span className="pill bad org-rule-tag">blocca</span>}
              {node.tag === 'open' && <span className="pill warn org-rule-tag">punto aperto</span>}
            </>
          );
          return (
            <li key={path} className={`org-rule level-${Math.min(level, 2)}`}>
              {node.children ? (
                <button
                  type="button"
                  className="org-rule-head"
                  aria-expanded={isOpen}
                  onClick={() => toggle(path)}
                >
                  <span className="org-twisty">{isOpen ? '▾' : '▸'}</span>
                  <span>
                    <RuleText text={node.text} query={needle} />
                  </span>
                  {level === 0 && (
                    <span className="muted small org-rule-count">{ruleCount(node)} regole</span>
                  )}
                </button>
              ) : (
                <div className="org-rule-head leaf">
                  <span className="org-twisty">·</span>
                  <span>
                    <RuleText text={node.text} query={needle} /> {tags}
                    {node.bands && (
                      <span className="org-bands">
                        {node.bands.map((band) => (
                          <span
                            key={band.label}
                            className={`org-band ${band.tone}`}
                            style={{ flexGrow: band.weight }}
                          >
                            <RuleText text={band.label} query={needle} />
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                </div>
              )}
              {node.children &&
                isOpen &&
                render(node.children, level + 1, `${path}/`, inherited || (!!needle && self(node)))}
            </li>
          );
        })}
      </ul>
    );
  };

  const tree = render(ORGANIZATION_RULES, 0, '', false);

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <h1>Regole</h1>
          <p className="muted">
            Le regole decise, per argomento. Aggiornate al {formatDate(ORGANIZATION_UPDATED)}.
          </p>
        </div>
      </div>
      <div className="card stack">
        <div className="org-toolbar">
          <label className="search-box">
            <Icon name="search" size={16} />
            <input
              type="search"
              placeholder="Cerca nelle regole… (IBAN, 50.000, margine)"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
          <button type="button" onClick={() => setOpen(new Set(allTopics))}>
            Espandi tutto
          </button>
          <button type="button" onClick={() => setOpen(new Set())}>
            Comprimi tutto
          </button>
          <span className="org-spacer" />
          <span className="muted small org-legend">
            <span className="pill bad">blocca</span> impedisce di procedere
            <span className="pill warn">punto aperto</span> non ancora deciso
          </span>
        </div>
        {tree ?? <p className="muted">Nessuna regola trovata.</p>}
      </div>
    </section>
  );
}
